import React from 'react';
import { Tag, Tooltip } from 'antd';

const AGE_POPULATIONS = ['新生儿', '婴儿', '儿童', '成人'];

const isAgePopulation = (value: string) => AGE_POPULATIONS.includes(value);

const ageGroupClass = (value: string) => {
  if (value === '新生儿') return 'neonate';
  if (value === '婴儿') return 'infant';
  if (value === '儿童') return 'child';
  if (value === '成人') return 'adult';
  return 'special';
};

const PopulationGlyph: React.FC<{ population: string }> = ({ population }) => {
  const group = ageGroupClass(population);
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      {group === 'neonate' ? (
        <>
          <path d="M8 20c1.8-4.4 4.5-6.6 8-6.6s6.2 2.2 8 6.6c-2.2 3.6-4.9 5.4-8 5.4S10.2 23.6 8 20Z" />
          <circle cx="16" cy="11" r="3.4" />
          <path d="m11 20 10-4M12 15.5l8.5 7" />
        </>
      ) : null}
      {group === 'infant' ? (
        <>
          <circle cx="16" cy="9.5" r="4" />
          <path d="M9.5 23.5c.6-6.4 2.8-9.6 6.5-9.6s5.9 3.2 6.5 9.6M11 18l-4 3M21 18l4 3" />
        </>
      ) : null}
      {group === 'child' ? (
        <>
          <circle cx="16" cy="7.5" r="3.4" />
          <path d="M10.5 23.5c.4-7.5 2.2-11.2 5.5-11.2s5.1 3.7 5.5 11.2M13.4 23.5v4M18.6 23.5v4M11.8 15.5 7.5 20M20.2 15.5l4.3 4.5" />
        </>
      ) : null}
      {group === 'adult' ? (
        <>
          <circle cx="16" cy="6.8" r="3.8" />
          <path d="M8.8 25.5c.8-8.5 3.2-12.8 7.2-12.8s6.4 4.3 7.2 12.8M16 13v13M10.5 17.2 6.5 23M21.5 17.2l4 5.8" />
        </>
      ) : null}
    </svg>
  );
};

interface PopulationIconGroupProps {
  populations: string[];
  maxVisible?: number;
  detailed?: boolean;
}

const PopulationIconGroup: React.FC<PopulationIconGroupProps> = ({
  populations,
  maxVisible = 4,
  detailed = false,
}) => {
  const normalized = Array.from(new Set(populations.map((item) => item.trim()).filter(Boolean)));
  const displayItems = normalized;

  if (!displayItems.length) {
    return <span className="population-icons__empty">尚未配置支持人群</span>;
  }

  if (detailed) {
    return (
      <div className="population-icons population-icons--detailed">
        {displayItems.map((population) => (
          isAgePopulation(population) ? (
            <span className="population-icon-label" key={population}>
              <span className={`population-icon population-icon--${ageGroupClass(population)}`} aria-hidden="true">
                <PopulationGlyph population={population} />
              </span>
              <span>{population}</span>
            </span>
          ) : <Tag key={population}>{population}</Tag>
        ))}
      </div>
    );
  }

  const visible = displayItems.slice(0, maxVisible);
  const hidden = displayItems.slice(maxVisible);
  return (
    <div className="population-icons" aria-label={`适用人群：${displayItems.join('、')}`}>
      {visible.map((population) => (
        isAgePopulation(population) ? (
          <Tooltip title={population} key={population}>
            <span
              className={`population-icon population-icon--${ageGroupClass(population)}`}
              tabIndex={0}
              role="img"
              aria-label={population}
            >
              <PopulationGlyph population={population} />
            </span>
          </Tooltip>
        ) : <Tag key={population} className="population-icons__special">{population}</Tag>
      ))}
      {hidden.length ? (
        <Tooltip title={hidden.join('、')}>
          <span className="population-icons__more" tabIndex={0} aria-label={`其他人群：${hidden.join('、')}`}>
            +{hidden.length}
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
};

export default PopulationIconGroup;
